'use client';

import { useDynamicConfig } from '@statsig/react-bindings';
import noop from 'lodash/noop';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import logToDatadog from '@/logger/logToDatadog';
import { useRefreshCart } from '@/queries/RefreshCart';
import {
  performSavePromotion,
  useSavePromotion,
} from '@/queries/SavePromotion';
import type {
  RefreshCart_cart_refreshCart,
  RefreshCart_cart_refreshCart_cart_lineItems,
} from '@/queries/types/RefreshCart';
import type { SavePromotion_cart_savePromotion } from '@/queries/types/SavePromotion';
import {
  readFromLocalStorage,
  writeToLocalStorage,
} from '@/utils/localStorage';

import { PROMOTION_CODE } from '../[locale]/store/_consts/discounts';

export interface CartContextValues {
  handleSetUrlDiscountCode: (code: string) => void;
  maxAddOnQuantityReachedByBaseSku: Record<string, boolean>;
  maxChargerQuantityReached: boolean;
  maxRingQuantityReached: boolean;
  refreshCart: RefreshCart_cart_refreshCart | null | undefined;
  refreshCartError: Error | null | undefined;
  refreshCartLoading: boolean;
  refreshCartSuccess: boolean;
  sitewideDiscounts: SavePromotion_cart_savePromotion[];
  urlPromotionData: SavePromotion_cart_savePromotion | null | undefined;
  urlPromotionError: Error | null | undefined;
  urlPromotionLoading: boolean;
  urlPromotionSuccess: boolean;
  urlDiscountCode: string;
}

const DEFAULT_CONTEXT_VALUES: CartContextValues = {
  handleSetUrlDiscountCode: noop,
  maxAddOnQuantityReachedByBaseSku: {},
  maxChargerQuantityReached: false,
  maxRingQuantityReached: false,
  refreshCart: null,
  refreshCartError: null,
  refreshCartLoading: true,
  refreshCartSuccess: false,
  sitewideDiscounts: [],
  urlPromotionData: null,
  urlPromotionError: null,
  urlPromotionLoading: true,
  urlPromotionSuccess: false,
  urlDiscountCode: '',
};

export const CartContext = createContext<CartContextValues>(
  DEFAULT_CONTEXT_VALUES,
);

const CartProvider = ({ children }: PropsWithChildren) => {
  const [sitewideDiscounts, setSitewideDiscounts] = useState<
    SavePromotion_cart_savePromotion[]
  >([]);

  const { value: sitewideDiscountConfig } =
    useDynamicConfig('sitewide_discount');
  const sitewideDiscountCodes = (sitewideDiscountConfig['sitewideDiscount'] ??
    '') as string | string[];

  useEffect(() => {
    const applySitewideDiscounts = async () => {
      try {
        const promises = Array.isArray(sitewideDiscountCodes)
          ? sitewideDiscountCodes.map((code) => performSavePromotion(code))
          : [performSavePromotion(sitewideDiscountCodes)];
        const results = await Promise.all(promises);
        setSitewideDiscounts(results);
      } catch (error) {
        logToDatadog({
          error,
          message: 'sitewide discount(s) failed to apply',
          module: 'discount',
        });
      }
    };
    void applySitewideDiscounts();
  }, [sitewideDiscountCodes]);

  const [urlDiscountCode, setUrlDiscountCode] = useState<string>('');
  const urlPromotionData = useSavePromotion(urlDiscountCode);

  // save to localstorage to persist from /discount paths redirect and refresh
  if (
    urlDiscountCode &&
    readFromLocalStorage(PROMOTION_CODE) !== urlDiscountCode
  ) {
    writeToLocalStorage(PROMOTION_CODE, urlDiscountCode);
  }
  const fetchingDiscounts =
    !sitewideDiscounts.length || urlPromotionData.isLoading;

  const refreshCart = useRefreshCart(fetchingDiscounts);

  const maxRingQuantityReached = useMemo(() => {
    const cart = refreshCart.data?.cart;
    const maxRingQuantity = cart?.maxRingQuantity;
    const ringCount = cart?.ringCount;
    if (typeof maxRingQuantity === 'number' && typeof ringCount === 'number') {
      return maxRingQuantity === ringCount;
    } else {
      return false;
    }
  }, [refreshCart]);

  const maxChargerQuantityReached = useMemo(() => {
    const cart = refreshCart.data?.cart;
    const maxChargerQuantity = cart?.maxChargerQuantity;
    const chargerCount = cart?.chargerCount;
    if (
      typeof maxChargerQuantity === 'number' &&
      typeof chargerCount === 'number'
    ) {
      return maxChargerQuantity === chargerCount;
    } else {
      return false;
    }
  }, [refreshCart]);

  const maxAddOnQuantityReachedByBaseSku = useMemo((): Record<
    string,
    boolean
  > => {
    const cart = refreshCart.data?.cart;
    const maxAddOnQuantityByBaseSku = cart?.maxAddOnQuantityByBaseSku ?? {};
    const lineItems =
      cart?.lineItems?.filter(
        (lineItem): lineItem is RefreshCart_cart_refreshCart_cart_lineItems =>
          Boolean(lineItem),
      ) ?? [];
    return lineItems.reduce((acc, cur) => {
      const skuCode = cur.skuCode;
      if (skuCode) {
        const baseSkus = Object.keys(maxAddOnQuantityByBaseSku);
        const baseSkuForLineItem =
          baseSkus.find((baseSku) => skuCode.startsWith(baseSku)) ?? '';
        const totalQuantityForLineItem = lineItems.reduce(
          (totalQuantity, lineItem) =>
            skuCode === lineItem.skuCode
              ? totalQuantity + (lineItem.quantity ?? 0)
              : totalQuantity,
          0,
        );
        const maxQuantityForLineItem =
          maxAddOnQuantityByBaseSku[baseSkuForLineItem];
        return {
          ...acc,
          [baseSkuForLineItem]:
            maxQuantityForLineItem === totalQuantityForLineItem,
        };
      }
      return acc;
    }, {});
  }, [refreshCart]);

  const onSetUrlDiscountCode = (code: string) => {
    setUrlDiscountCode(code);
  };

  const value: CartContextValues = useMemo(
    () => ({
      handleSetUrlDiscountCode: onSetUrlDiscountCode,
      maxAddOnQuantityReachedByBaseSku,
      maxChargerQuantityReached,
      maxRingQuantityReached,
      refreshCart: refreshCart.data,
      refreshCartError: refreshCart.error,
      refreshCartLoading: refreshCart.isLoading,
      refreshCartSuccess: refreshCart.isSuccess,
      sitewideDiscounts,
      urlPromotionData: urlPromotionData.data,
      urlPromotionError: urlPromotionData.error,
      urlPromotionLoading: urlPromotionData.isLoading,
      urlPromotionSuccess: urlPromotionData.isSuccess,
      urlDiscountCode,
    }),
    [
      maxAddOnQuantityReachedByBaseSku,
      maxChargerQuantityReached,
      maxRingQuantityReached,
      refreshCart,
      sitewideDiscounts,
      urlPromotionData,
      urlDiscountCode,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCartContext = () => useContext(CartContext);

export default CartProvider;
